import React, { useState, useEffect } from 'react';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useTiersGameContext } from '../context/TiersGameContext';
import { Paper, Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Typography, Modal, Button, TablePagination, Tab } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import useNavigateToTop from '../../../hooks/general/useNavigateToTop';
import TiebreakerModal from '../../GameModals/TiebreakerInfoModal/TiebreakerModal';
import LoadingScreen from '../../LoadingScreen/LoadingScreen';
import { DateTime } from 'luxon';
import TiersUserSelectionModal from './TiersUserSelectionModal';
import IntervalDropdown from '../../GamePageComponents/Dropdowns/IntervalDropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from 'lodash';

const TiersLeaderboard = () => {
    const {
        masterPoolData,
        isGameOver,
        myLeaderboardInfo,
        leaderboardData,
        myUserId,
        intervalInfo,
        isUserInThisPool,
    } = useUserAndGameContext();

    const {
        theCurrentInterval,
        oneIntervalEvent,
        firstInterval,
        lastInterval,
        intervalsInfo,
        hasThisGamesFirstIntervalDeadlinePassed,
    } = useTiersGameContext();

    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [sortBy, setSortBy] = useState('rank');
    const [sortOrder, setSortOrder] = useState('asc');
    const [sortedColumn, setSortedColumn] = useState('rank');
    const [tiebreakerInfoOpen, setTiebreakerInfoOpen] = useState(false);
    const [leaderboardInterval, setLeaderboardInterval] = useState('');
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [userModalData, setUserModalData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const timeInNewYorkNow = DateTime.now().setZone('America/New_York');
    const thisIntervalsDeadline = intervalsInfo?.deadline;
    const thisIntervalsDeadlineInNewYork = thisIntervalsDeadline ? DateTime.fromISO(thisIntervalsDeadline, { zone: 'America/New_York' }) : null;
    const hasThisIntervalsDeadlinePassed = thisIntervalsDeadlineInNewYork ? thisIntervalsDeadlineInNewYork < timeInNewYorkNow : false;

    const intervalName = intervalInfo?.info?.find(info => info.interval === parseInt(leaderboardInterval))?.label;
    
    // Function to get interval from URL query parameters
    const getIntervalFromURL = () => {
        const params = new URLSearchParams(location.search);
        const intervalParam = params.get('interval');
        
        if (intervalParam === 'Overall') {
            return 'Overall';
        } else if (intervalParam) {
            const numInterval = parseInt(intervalParam, 10);
            if (!isNaN(numInterval)) {
                return numInterval;
            }
        }
        
        // Default to current interval if not found in URL
        return null;
    };
    
    // Function to update URL when interval changes
    const updateURLInterval = (newInterval) => {
        const params = new URLSearchParams(location.search);
        params.set('interval', newInterval);
        navigate({ search: params.toString() });
    };

    // Single useEffect to handle initial interval setting and URL changes
    useEffect(() => {
        // Get interval from URL
        const intervalFromURL = getIntervalFromURL();
        
        // Handle game over condition first
        // if ((isGameOver && !oneIntervalEvent) || oneIntervalEvent) {
        //     if (leaderboardInterval !== 'Overall') {
        //         setLeaderboardInterval('Overall');
        //         if (intervalFromURL !== 'Overall') {
        //             updateURLInterval('Overall');
        //         }
        //     }
        //     return;
        // }
        
        // If URL has a valid interval, use it
        if (intervalFromURL) {
            // For numeric intervals, validate they're in range
            if (intervalFromURL !== 'Overall') {
                if (intervalFromURL < firstInterval) {
                    setLeaderboardInterval(firstInterval);
                    updateURLInterval(firstInterval);
                } else if (intervalFromURL > lastInterval) {
                    setLeaderboardInterval(lastInterval);
                    updateURLInterval(lastInterval);
                } else {
                    setLeaderboardInterval(intervalFromURL);
                }
            } else {
                // Handle 'Overall' case
                setLeaderboardInterval('Overall');
            }
        } else {
            // No interval in URL, use the current interval
            if (theCurrentInterval >= firstInterval && theCurrentInterval <= lastInterval) {
                setLeaderboardInterval(theCurrentInterval);
                updateURLInterval(theCurrentInterval);
            } else if (theCurrentInterval < firstInterval) {
                setLeaderboardInterval(firstInterval);
                updateURLInterval(firstInterval);
            } else if (theCurrentInterval > lastInterval) {
                setLeaderboardInterval(lastInterval);
                updateURLInterval(lastInterval);
            }
        }
    }, [location.search, isGameOver, oneIntervalEvent, firstInterval, lastInterval, theCurrentInterval]);

    function findRank(entry) {
        if (leaderboardInterval === 'Overall') {
            return entry.rank ?? '-';
        } 
    
        // Check if intervalData exists and is an array
        if (!entry.intervalData || !Array.isArray(entry.intervalData)) {
            return '-';
        }
    
        // Find the interval object with the matching interval number
        const intervalObj = entry.intervalData.find(interval => interval.interval === parseInt(leaderboardInterval));
        
        // Return the rank if found, otherwise '-'
        return intervalObj?.rank ?? '-';
    }
    
    function processStat(columnName, entry) {
        if (columnName === 'score') {
            return findScore(entry);
        } else if (columnName === 'alive') {
            return findAlive(entry);
        } 
        // Add more conditions for other columns as needed
    }

    function findScore(entry) {
        let score;
        
        if (leaderboardInterval === 'Overall') {
            score = entry?.overallScore;
        } else {
            // Check if intervalData exists and is an array
            if (!entry.intervalData || !Array.isArray(entry.intervalData)) {
                return '-';
            }
    
            // Find the interval object with the matching interval number
            const intervalObj = entry.intervalData.find(interval => interval.interval === parseInt(leaderboardInterval));
            
            // Get the score if found
            score = intervalObj?.intervalScore;
        }
        
        // If score is undefined or null, return '-'
        if (score === undefined || score === null) {
            return '-';
        }
        
        // Format the score with proper golf notation
        if (score === 0) {
            return 'E'; // Even par
        } else if (score > 0) {
            return '+' + score; // Over par, add plus sign
        } else {
            return score.toString(); // Under par, keep negative sign
        }
    }

    // Check all the players for this interval. If they have cut: true or withdrawn: true, they are NOT alive
    function findAlive(entry) {
        if (leaderboardInterval === 'Overall') {
            return entry?.alive;
        } else {
            // Check if intervalData exists and is an array
            if (!entry.intervalData || !Array.isArray(entry.intervalData)) {
                return '-';
            }
            
            // Find the interval object with the matching interval number
            const intervalObj = entry.intervalData.find(interval => 
                interval.interval === parseInt(leaderboardInterval)
            );
            
            // Get the total number of golfers to pick
            const golfersToPick = intervalsInfo?.selections?.reduce(
                (acc, tier) => acc + tier.picks, 0
            ) || 0;
            
            // Count the number of golfers who do NOT have cut: true or withdrawn: true
            // Use optional chaining and provide a default empty array if players is undefined
            const golfersAlive = intervalObj?.players?.filter(
                pick => pick.cut !== true && 
                    pick.withdrawn !== true && 
                    pick.id && 
                    !pick.id.startsWith('no-pick')
            ) || [];
            
            // Return the count with proper formatting, ensuring we return "0/N" instead of "undefined/N"
            return `${golfersAlive.length || 0}/${golfersToPick}`;
        }
    }

    const getFilteredMyInfo = () => {
        if (!myLeaderboardInfo?.myInfo || !Array.isArray(myLeaderboardInfo.myInfo)) {
            return [];
        }
        
        return myLeaderboardInfo.myInfo.filter(entry => {
            // First check if entry exists
            if (!entry) return false;
            
            // For Overall leaderboard view
            if (leaderboardInterval === 'Overall') {
                // If first interval deadline has NOT passed yet, show everyone
                if (!hasThisGamesFirstIntervalDeadlinePassed) {
                    return true;
                }
                
                // After first interval deadline has passed, only show users with first interval data
                const hasFirstIntervalData = entry?.intervalData?.some(
                    interval => interval.interval === firstInterval
                );
                
                return hasFirstIntervalData || false;
            } 
            // For specific interval view
            else {
                // Only show users with data for this specific interval
                // if the interval deadline has passed
                const intervalNumber = parseInt(leaderboardInterval, 10);
                
                if (hasThisIntervalsDeadlinePassed) {
                    return entry?.intervalData?.some(
                        interval => interval.interval === intervalNumber
                    ) || false;
                }
                
                // If deadline hasn't passed, show all users
                return true;
            }
        });
    };

    // Add pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle interval change from dropdown
    const handleIntervalChange = (newInterval) => {
        updateURLInterval(newInterval);
    };

    // Add useEffect to reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [leaderboardInterval, sortBy, sortOrder]);

    const handleSort = (column) => {
        // If clicking the same column, toggle the sortOrder
        if (sortBy === column) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortBy(column);
          setSortOrder('asc'); // Reset to ascending when sorting a new column
        }
    };

    // Filter columns based on leaderboardInterval
    const visibleColumns = React.useMemo(() => {
        const leaderboardColumns = masterPoolData?.leaderboardInfo || [];
        
        return leaderboardColumns.filter(column => {
            // Hide columns marked with hideOnOverall when in Overall view
            if (leaderboardInterval === 'Overall' && column.hideOnOverall) {
                return false;
            }
            return true;
        });
    }, [masterPoolData, leaderboardInterval]);

    // Filter the leaderboard data based on the selected interval
    // If its overall leaderboard, if a user has isDisqualified true,
    // OR if hasThisGamesFirstIntervalDeadlinePassed is true, and the user does not have that interval's ranking data,
    // then filter them OUT of the leaderboard (so they don't show up at all)
    const getFilteredLeaderboardData = () => {
        if (!leaderboardData || !Array.isArray(leaderboardData)) {
            return [];
        }
        
        return leaderboardData.filter(entry => {
            // First check if entry exists
            if (!entry) return false;
            
            // For Overall leaderboard view
            if (leaderboardInterval === 'Overall') {
                // If first interval deadline has NOT passed yet, show everyone
                if (!hasThisGamesFirstIntervalDeadlinePassed) {
                    return true;
                }
                
                // After first interval deadline has passed, only show users with first interval data
                const hasFirstIntervalData = entry?.intervalData?.some(
                    interval => interval.interval === firstInterval
                );
                
                return hasFirstIntervalData || false;
            } 
            // For specific interval view
            else {
                // Only show users with data for this specific interval
                // if the interval deadline has passed
                const intervalNumber = parseInt(leaderboardInterval, 10);
                
                if (hasThisIntervalsDeadlinePassed) {
                    return entry?.intervalData?.some(
                        interval => interval.interval === intervalNumber
                    ) || false;
                }
                
                // If deadline hasn't passed, show all users
                return true;
            }
        });
    };

    // Replace the existing filteredLeaderboardData implementation with this:
    const filteredLeaderboardData = getFilteredLeaderboardData();

    // Updated sorting logic for leaderboard data
    const sortedLeaderboardData = [...filteredLeaderboardData]?.sort((a, b) => {
        if (leaderboardInterval === 'Overall') {

        // For overall ranking, sort by rank (null values go last)
        if (a.rank === null && b.rank === null) return 0;
        if (a.rank === null) return 1; // a is null, move to end
        if (b.rank === null) return -1; // b is null, move to end
        return a.rank - b.rank; // Normal numeric sort
        } else {
        // For specific interval, find the relevant interval data
        const intervalA = a.intervalData?.find(data => data.interval === parseInt(leaderboardInterval));
        const intervalB = b.intervalData?.find(data => data.interval === parseInt(leaderboardInterval));
        
        // Handle cases where interval data doesn't exist for one or both entries
        if (!intervalA && !intervalB) return 0;
        if (!intervalA) return 1; // a doesn't have this interval, move to end
        if (!intervalB) return -1; // b doesn't have this interval, move to end
        
        // Handle cases where interval exists but rank might be null
        if (intervalA.rank === null && intervalB.rank === null) return 0;
        if (intervalA.rank === null) return 1; // a rank is null, move to end
        if (intervalB.rank === null) return -1; // b rank is null, move to end
        
        // Normal numeric sort by interval rank
        return intervalA.rank - intervalB.rank;
        }
    });

    const paginatedData = sortedLeaderboardData?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
      
    if (leaderboardInterval === '') {
        return <LoadingScreen />;
    }

    return (
        <>
            {theCurrentInterval > firstInterval && (
                <IntervalDropdown
                    interval={leaderboardInterval.toString()} // Convert to string for the dropdown
                    setInterval={handleIntervalChange}
                    firstInterval={firstInterval}
                    lastInterval={lastInterval}
                    theCurrentInterval={theCurrentInterval}
                    intervalInfo={intervalInfo}
                    includeOverall={true}
                />
            )}

            {isUserInThisPool && getFilteredMyInfo()?.length === 0 && (
                <Typography
                    sx={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 500,
                        color: '#CC293C',
                        textAlign: 'center',
                        width: isMobile ? '90%' : '50%',
                        maxWidth: isMobile ? '400px' : '500px',
                        margin: '0 auto',
                    }}
                >
                    {leaderboardInterval === 'Overall' 
                        ? 'Only entrants who have participated in all tournaments will appear on this leaderboard.' 
                        : `You did not have any entry in ${
                            intervalName?.startsWith('The') ? intervalName : `The ${intervalName}`
                            }.`}
                </Typography>
            )}

            <Box sx={{ maxWidth: '750px', margin: '0 auto' }}>
                <TablePagination
                    rowsPerPageOptions={[50, 100]}
                    component="div"
                    count={sortedLeaderboardData?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={Math.min(page, Math.max(0, Math.ceil((sortedLeaderboardData?.length || 0) / rowsPerPage) - 1))}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows"
                    sx={{
                        backgroundColor: 'white',
                        '& .MuiTablePagination-toolbar': {
                            color: '#002129',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
                            color: '#002129',
                        }
                    }}
                />
            </Box>

            <TableContainer component={Paper} style={{overflowX: "clip", maxWidth:'750px', margin:'0 auto', marginBottom: '20px'}}>
                <Table>
                {getFilteredMyInfo()?.length > 0 && (
                    <>
                        {getFilteredMyInfo()?.map((entry, index) => (
                                <TableHead key={index} sx={{ backgroundColor: '#F0F1F1' }}>
                                    <TableRow>
                                        <TableCell className="pickem-leaderboard-table-cells" sx={{ textAlign: 'center', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>
                                            {findRank(entry)}
                                        </TableCell>
                                        <TableCell className="pickem-leaderboard-table-cells" sx={{ textAlign: 'left', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>
                                            <Box 
                                                sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setUserModalOpen(true);
                                                    setUserModalData(entry);
                                                }}
                                            >
                                                {entry.username}
                                                {entry?.logo && (
                                                    <img
                                                        src={entry.logo}
                                                        alt="User Logo"
                                                        style={{ 
                                                            width: isMobile ? '25px' : '30px', 
                                                            height: isMobile ? '25px' : '30px', 
                                                            marginLeft: '5px' 
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        {visibleColumns?.map((column) => (
                                            <TableCell key={column.name} className="pickem-leaderboard-table-cells" sx={{ textAlign: 'center', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>
                                                {processStat(column.name, entry)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                            ))}
                        </>
                    )}
                    <TableHead sx={{ backgroundColor: '#002129' }}>
                        <TableRow>
                            <TableCell className="pickem-leaderboard-table-cells"
                                onClick={() => {
                                handleSort('rank');
                                setSortedColumn('rank');
                                }}
                                sx={{ width: '57px', color: sortedColumn === 'rank' ? '#FFC60A' : '#fff' , fontWeight: 700, textAlign: 'center', position: 'relative', cursor: 'pointer', fontSize: isMobile ? '14px' : '22px', textDecoration: 'underline'}}
                            >
                                RANK
                            </TableCell>
                            <TableCell className="pickem-leaderboard-table-cells"
                                onClick={() => {
                                handleSort('username');
                                setSortedColumn('username');
                                }}
                                sx={{ width: '160px', color: sortedColumn === 'username' ? '#FFC60A' : '#fff' , fontWeight: 700, textAlign: 'left', position: 'relative', cursor: 'pointer', fontSize: isMobile ? '14px' : '22px',  textDecoration: 'underline'}}
                            >
                                PLAYER
                            </TableCell>
                            {visibleColumns.map((column) => (
                                <TableCell 
                                    key={column.name}
                                    className="pickem-leaderboard-table-cells"
                                    onClick={() => {
                                    handleSort(column.name);
                                    setSortedColumn(column.name);
                                    }}
                                    sx={{ 
                                        color: sortedColumn === column.name ? '#FFC60A' : '#fff', 
                                        fontWeight: 700, 
                                        textAlign: 'center', 
                                        position: 'relative', 
                                        cursor: 'pointer', 
                                        fontSize: isMobile ? '14px' : '22px', 
                                        textDecoration: 'underline'
                                    }}
                                >
                                    {column.name.toUpperCase()}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData?.map((entry, index) => (
                            <TableRow
                                key={`${entry.userId}-${entry.username}`}
                                sx={{ backgroundColor: entry.userId === myUserId ? '#FFF6DA' : index % 2 === 0 ? '#FFF' : '#F0F1F1' }}
                            >
                                <TableCell className="pickem-leaderboard-table-cells" sx={{ textAlign: 'center', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>
                                    {findRank(entry)}
                                </TableCell>
                                <TableCell 
                                    className="pickem-leaderboard-table-cells" 
                                    sx={{ textAlign: 'left', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}
                                >
                                    <Box 
                                        sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: ((leaderboardInterval === 'Overall' && theCurrentInterval > 1) || hasThisIntervalsDeadlinePassed) ? 'underline' : 'none', cursor: ((leaderboardInterval === 'Overall' && theCurrentInterval > 1) || hasThisIntervalsDeadlinePassed) ? 'pointer' : 'default' }}
                                        onClick={() => {
                                            if ((leaderboardInterval === 'Overall' && theCurrentInterval > 1) || hasThisIntervalsDeadlinePassed) {
                                                setUserModalOpen(true);
                                                setUserModalData(entry);
                                            }
                                        }}    
                                    >
                                        {entry.username}
                                        {entry?.logo && (
                                            <img
                                                src={entry.logo}
                                                alt="User Logo"
                                                style={{ 
                                                    width: isMobile ? '25px' : '30px', 
                                                    height: isMobile ? '25px' : '30px', 
                                                    marginLeft: '5px' 
                                                }}
                                            />
                                        )}
                                    </Box>
                                </TableCell>

                                {visibleColumns?.map((column) => (
                                    <TableCell key={column.name} className="pickem-leaderboard-table-cells" sx={{ textAlign: 'center', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>
                                        {processStat(column.name, entry)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>

                </Table>
            </TableContainer>
            <Box sx={{ maxWidth: '750px', margin: '0 auto' }}>
                <TablePagination
                    rowsPerPageOptions={[50, 100]}
                    component="div"
                    count={sortedLeaderboardData?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={Math.min(page, Math.max(0, Math.ceil((sortedLeaderboardData?.length || 0) / rowsPerPage) - 1))}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows"
                    sx={{
                        backgroundColor: 'white',
                        '& .MuiTablePagination-toolbar': {
                            color: '#002129',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
                            color: '#002129',
                        }
                    }}
                />
            </Box>

            {userModalOpen && (
               <TiersUserSelectionModal
                    open={userModalOpen}
                    onClose={() => {
                        setUserModalOpen(false);
                        setUserModalData(null);
                    }}
                    entry={userModalData}
                    interval={leaderboardInterval}
                />
            )}
        </>
    );
};

export default TiersLeaderboard;